/**
 * State global des mini-drawers de chat ouverts simultanément (style Facebook).
 *
 * MAX_OPEN drawers rendus côte à côte à droite. Au-delà, les convs
 * supplémentaires sont "minimisées" en bulles empilables à gauche.
 * Une seule conv à la fois est "focused" (recevra les raccourcis Esc, etc.).
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface OpenChat {
  convId: number;
  label: string;         // nom affiché dans le header du drawer
  minimized: boolean;    // true = réduit en bulle (pas rendu en drawer plein)
  openedAt: number;      // timestamp — pour tri "plus récent = plus à droite"
}

export interface OpenChatsState {
  chats: OpenChat[];
  focusedConvId: number | null;
  searchOpen: boolean;   // recherche globale Ctrl+K
}

export const MAX_OPEN_DRAWERS = 3;

const STORAGE_KEY = 'openChats:v1';

function loadFromStorage(): Partial<OpenChatsState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.chats)) return {};
    // On garde uniquement les convs récentes (< 24h) pour éviter d'empiler
    // à l'infini au fil des jours.
    const now = Date.now();
    const chats: OpenChat[] = parsed.chats
      .filter((c: any) => c && typeof c.convId === 'number'
        && typeof c.label === 'string'
        && (now - (c.openedAt || 0)) < 24 * 60 * 60 * 1000)
      .slice(0, 8);
    return {
      chats,
      focusedConvId: typeof parsed.focusedConvId === 'number' ? parsed.focusedConvId : null,
    };
  } catch { return {}; }
}

function persist(state: OpenChatsState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      chats: state.chats,
      focusedConvId: state.focusedConvId,
    }));
  } catch { /* quota exceeded */ }
}

const initialState: OpenChatsState = {
  chats: [],
  focusedConvId: null,
  searchOpen: false,
  ...loadFromStorage(),
};

const slice = createSlice({
  name: 'openChats',
  initialState,
  reducers: {
    openChat(state, action: PayloadAction<{ convId: number; label?: string }>) {
      const { convId, label } = action.payload;
      const existing = state.chats.find(c => c.convId === convId);
      if (existing) {
        existing.minimized = false;
        if (label) existing.label = label;
      } else {
        // Nouvelle conv — pousser en tête (la plus récente = la plus à droite)
        state.chats.unshift({
          convId,
          label: label || `Conversation ${convId}`,
          minimized: false,
          openedAt: Date.now(),
        });
        // Trim à 8 max (au-delà, ferme les plus anciennes)
        if (state.chats.length > 8) state.chats = state.chats.slice(0, 8);
      }
      state.focusedConvId = convId;
      persist(state);
    },
    closeChat(state, action: PayloadAction<number>) {
      const convId = action.payload;
      state.chats = state.chats.filter(c => c.convId !== convId);
      if (state.focusedConvId === convId) {
        state.focusedConvId = state.chats[0]?.convId ?? null;
      }
      persist(state);
    },
    closeAll(state) {
      state.chats = [];
      state.focusedConvId = null;
      persist(state);
    },
    closeFocused(state) {
      if (!state.focusedConvId) return;
      const fid = state.focusedConvId;
      state.chats = state.chats.filter(c => c.convId !== fid);
      state.focusedConvId = state.chats[0]?.convId ?? null;
      persist(state);
    },
    focusChat(state, action: PayloadAction<number>) {
      const convId = action.payload;
      const existing = state.chats.find(c => c.convId === convId);
      if (!existing) return;
      existing.minimized = false;
      state.focusedConvId = convId;
      persist(state);
    },
    toggleMinimize(state, action: PayloadAction<number>) {
      const chat = state.chats.find(c => c.convId === action.payload);
      if (chat) {
        chat.minimized = !chat.minimized;
        if (chat.minimized && state.focusedConvId === chat.convId) {
          state.focusedConvId = state.chats.find(c => !c.minimized)?.convId ?? null;
        } else if (!chat.minimized) {
          state.focusedConvId = chat.convId;
        }
        persist(state);
      }
    },
    focusNext(state) {
      if (state.chats.length === 0) return;
      const idx = state.chats.findIndex(c => c.convId === state.focusedConvId);
      const next = state.chats[(idx + 1) % state.chats.length];
      if (next) {
        next.minimized = false;
        state.focusedConvId = next.convId;
        persist(state);
      }
    },
    focusPrevious(state) {
      if (state.chats.length === 0) return;
      const idx = state.chats.findIndex(c => c.convId === state.focusedConvId);
      const prev = state.chats[(idx - 1 + state.chats.length) % state.chats.length];
      if (prev) {
        prev.minimized = false;
        state.focusedConvId = prev.convId;
        persist(state);
      }
    },
    setSearchOpen(state, action: PayloadAction<boolean>) {
      state.searchOpen = action.payload;
    },
  },
});

export const {
  openChat,
  closeChat,
  closeAll,
  closeFocused,
  focusChat,
  toggleMinimize,
  focusNext,
  focusPrevious,
  setSearchOpen,
} = slice.actions;

export default slice.reducer;
